/**
 * devices.repository.ts — users/{uid}/devices/{deviceId} 본인 기기 읽기·쓰기.
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 기기 · §5-2 rules · §7-3 기기 3대 · §7-5 강제 로그아웃.
 *  - 문서 ID = 브라우저가 만든 무작위 기기 ID(lib/sessionLocal getDeviceId). 비즈니스 키가 따로 없다.
 *  - rules(A 묶음): 본인 create·update(필드 화이트리스트 validDevice), delete 금지, 읽기 = 본인·전체 admin.
 *  - 해제 = status:'revoked' + revoke_requested_at. 그 기기의 앱이 자기 문서를 듣다가 스스로 로그아웃한다.
 *  - 다른 사람 기기 조작(관리자)은 admin_ops(함수) 경유 — 여기에 넣지 않는다.
 *
 * ⚠️ rules validDevice 의 키 목록에 없는 필드를 쓰면 저장이 거부된다. 필드를 늘리면 rules 와 함께.
 * 함수가 revoked_at(해제 시각)·updated_at(관리자 자동 로그인 해제)을 merge 로 덧붙인다 — rules validDevice 가
 *    두 키를 허용하므로 남아 있어도 본인 update 는 통과한다. 덮어쓰기(setDoc)는 처음 만들 때만 쓴다(A 계약 2026-10-23).
 *    revoked_at 은 1년 정리 기준(index.ts cleanup)이라 본인 해제 때 함께 찍고, 다시 활성화할 때 비운다.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  Timestamp,
  type DocumentData,
} from "firebase/firestore";
import { db } from "./firebase";
import { isDeviceRevokeReason, type DeviceDoc, type DeviceKind, type DeviceStatus } from "@/domain/session";
import { DEVICE_NAME_MAX, normalizeDeviceName } from "@/domain/device_info";

/** 런타임 화이트리스트 — DeviceKind·DeviceStatus union 과 같이 */
const VALID_KINDS: readonly DeviceKind[] = ["pc", "mo"];
const VALID_STATUSES: readonly DeviceStatus[] = ["active", "revoked"];

function toDate(v: unknown): Date | null {
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

export function deviceFromFirestore(id: string, d: DocumentData): DeviceDoc {
  const kind = VALID_KINDS.includes(d.kind) ? (d.kind as DeviceKind) : "pc";
  if (!VALID_KINDS.includes(d.kind)) console.warn("[devices] 알 수 없는 kind", id, d.kind);
  // 모르는 값은 revoked — 활성으로 잘못 세지 않게(통합관리 목록·3대 계산 공통, users.repository 와 합침)
  const status = VALID_STATUSES.includes(d.status) ? (d.status as DeviceStatus) : "revoked";
  if (!VALID_STATUSES.includes(d.status)) console.warn("[devices] 알 수 없는 status", id, d.status);
  return {
    id,
    name: typeof d.name === "string" && d.name ? d.name : "이름 없는 기기",
    browser: typeof d.browser === "string" ? d.browser : "",
    os: typeof d.os === "string" ? d.os : "",
    kind,
    created_at: toDate(d.created_at),
    last_seen_at: toDate(d.last_seen_at),
    auto_login: d.auto_login !== false,
    shared: d.shared === true,
    status,
    revoke_requested_at: toDate(d.revoke_requested_at),
    revoked_by: typeof d.revoked_by === "string" ? d.revoked_by : null,
    revoke_reason: isDeviceRevokeReason(d.revoke_reason) ? d.revoke_reason : null,
    push_token: typeof d.push_token === "string" && d.push_token ? d.push_token : null,
    session_id: typeof d.session_id === "string" ? d.session_id : "",
  };
}

function devicesCol(uid: string) {
  return collection(db, "users", uid, "devices");
}
function deviceRef(uid: string, deviceId: string) {
  return doc(db, "users", uid, "devices", deviceId);
}

/** 내 기기 전체(해제된 것 포함) 1회 읽기 */
export async function listMyDevices(uid: string): Promise<DeviceDoc[]> {
  const snap = await getDocs(devicesCol(uid));
  return snap.docs.map((s) => deviceFromFirestore(s.id, s.data()));
}

export async function getMyDevice(uid: string, deviceId: string): Promise<DeviceDoc | null> {
  const snap = await getDoc(deviceRef(uid, deviceId));
  return snap.exists() ? deviceFromFirestore(snap.id, snap.data()) : null;
}

/** 내 기기 목록 구독(해제된 것 포함 — 화면에서 활성만 거른다). 몇 개뿐이라 정렬은 클라이언트에서 */
export function watchMyDevices(
  uid: string,
  cb: (list: DeviceDoc[]) => void,
  onError?: (e: Error) => void,
): () => void {
  return onSnapshot(
    devicesCol(uid),
    (snap) => cb(snap.docs.map((s) => deviceFromFirestore(s.id, s.data()))),
    (e) => onError?.(e),
  );
}

/** 이 기기 문서 1개 구독 — 없으면 null */
export function watchMyDevice(
  uid: string,
  deviceId: string,
  cb: (d: DeviceDoc | null) => void,
  onError?: (e: Error) => void,
): () => void {
  return onSnapshot(
    deviceRef(uid, deviceId),
    (snap) => cb(snap.exists() ? deviceFromFirestore(snap.id, snap.data()) : null),
    (e) => onError?.(e),
  );
}

export interface RegisterDeviceInput {
  name: string;
  browser: string;
  os: string;
  kind: DeviceKind;
  session_id: string;
  /** 이미 있는 문서(다시 로그인). 활성이면 이름·자동 로그인(Q7)·푸시 토큰 유지, 해제 상태면 다시 등록 */
  existing: DeviceDoc | null;
}

/**
 * 로그인 때 이 기기를 활성으로 등록(또는 다시 활성). 해제 표시(revoke_requested_at)는 지운다 —
 * 지우지 않으면 구독이 곧바로 로그아웃시킨다.
 * 새 기기 = setDoc(새 문서), 이미 있는 문서 = updateDoc(함수가 쓴 필드를 덮어쓰지 않게, A 계약).
 */
export async function registerMyDevice(uid: string, deviceId: string, input: RegisterDeviceInput): Promise<void> {
  const ex = input.existing;
  const ref = deviceRef(uid, deviceId);
  if (!ex) {
    await setDoc(ref, {
      name: normalizeDeviceName(input.name) || "이름 없는 기기",
      browser: input.browser.slice(0, DEVICE_NAME_MAX),
      os: input.os.slice(0, DEVICE_NAME_MAX),
      kind: input.kind,
      created_at: serverTimestamp(),
      last_seen_at: serverTimestamp(),
      auto_login: true,
      shared: false,
      status: "active",
      revoke_requested_at: null,
      revoked_by: null,
      push_token: null,
      session_id: input.session_id,
    });
    return;
  }
  const reactivate = ex.status !== "active";
  await updateDoc(ref, {
    browser: input.browser.slice(0, DEVICE_NAME_MAX),
    os: input.os.slice(0, DEVICE_NAME_MAX),
    kind: input.kind,
    last_seen_at: serverTimestamp(),
    status: "active",
    revoke_requested_at: null,
    revoked_by: null,
    revoked_at: null,
    revoke_reason: null,
    session_id: input.session_id,
    // 해제됐던 기기를 다시 쓰면 새로 등록한 것으로 본다(처음 등록 시각·자동 로그인 초기화, 토큰은 함수가 이미 지움)
    ...(reactivate ? { created_at: serverTimestamp(), auto_login: true, push_token: null } : {}),
  });
}

/** 마지막 접속 갱신(앱 열 때·자동 로그인). 호출부에서 15분 이내 중복을 거른다 */
export async function touchMyDevice(uid: string, deviceId: string): Promise<void> {
  await updateDoc(deviceRef(uid, deviceId), { last_seen_at: serverTimestamp() });
}

export async function renameMyDevice(uid: string, deviceId: string, name: string): Promise<void> {
  const n = normalizeDeviceName(name);
  if (!n) throw new Error("기기 이름을 입력하세요.");
  await updateDoc(deviceRef(uid, deviceId), { name: n });
}

/** 자동 로그인 켜기·끄기(D-3). 그 기기의 앱이 값을 보고 저장 방식을 바꾼다(§7-5) */
export async function setMyDeviceAutoLogin(uid: string, deviceId: string, on: boolean): Promise<void> {
  await updateDoc(deviceRef(uid, deviceId), { auto_login: on });
}

/**
 * 기기 해제 — 로그인 단계에서 3대가 꽉 찼을 때 본인이 고른 기기를 비운다(E-3, §7-3 클라이언트 직접).
 * 기기관리 화면의 해제·로그아웃은 접속 기록 처리자 표기를 위해 account_ops(self.device_revoke)를 쓴다.
 */
export async function releaseMyDevice(uid: string, deviceId: string): Promise<void> {
  await updateDoc(deviceRef(uid, deviceId), {
    status: "revoked",
    revoke_requested_at: serverTimestamp(),
    revoked_at: serverTimestamp(),
    revoked_by: uid,
  });
}

/** 이 기기 푸시 토큰 기록(D-2 이 기기 앱 푸시). null = 끔 */
export async function setMyDevicePushToken(uid: string, deviceId: string, token: string | null): Promise<void> {
  await updateDoc(deviceRef(uid, deviceId), { push_token: token });
}
